import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../config/api';
import './OfferUploadPage.css';

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const OfferUploadPage = () => {
  const { linkId, addressSlug } = useParams();
  // Optional cosmetic address segment (/offer/{addressSlug}/{linkId}) — kept when navigating.
  const offerBasePath = addressSlug ? `/offer/${addressSlug}/${linkId}` : `/offer/${linkId}`;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Session token precedence: URL `?session=` wins (just-verified case) and is
  // mirrored into localStorage; otherwise fall back to the persisted copy so a
  // returning submitter skips the OTP step across visits. JWT has no `exp`;
  // revocation happens when the agent disables the parent offer-upload link.
  const sessionFromUrl = searchParams.get('session') || '';
  const [sessionToken, setSessionToken] = useState(() => {
    if (sessionFromUrl) return sessionFromUrl;
    try { return localStorage.getItem(`offerLinkSession:${linkId}`) || ''; }
    catch { return ''; }
  });

  useEffect(() => {
    if (sessionFromUrl) {
      try { localStorage.setItem(`offerLinkSession:${linkId}`, sessionFromUrl); }
      catch { /* storage unavailable — URL already carries it */ }
      setSessionToken(sessionFromUrl);
    }
  }, [sessionFromUrl, linkId]);

  const [me, setMe] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Submitter's own past offers on this link. 'mode' picks between creating a
  // new offer vs appending to one of these. 'add' is only selectable when
  // submissions is non-empty.
  const [submissions, setSubmissions] = useState([]);
  const [mode, setMode] = useState('new'); // 'new' | 'add'
  const [targetOfferId, setTargetOfferId] = useState('');
  // Offer terms for a new offer — same fields as the listing agent's internal
  // "Upload Offer Package" form (SellerOffers.js). Not used when appending docs.
  const emptyOfferDetails = {
    offerPrice: '', earnestMoney: '', downPaymentPercent: '',
    financingType: 'CONVENTIONAL', closingDays: '30', notes: '',
  };
  const [offerDetails, setOfferDetails] = useState(emptyOfferDetails);
  const setDetail = (field) => (e) => setOfferDetails((prev) => ({ ...prev, [field]: e.target.value }));
  const [justSubmittedMessage, setJustSubmittedMessage] = useState('');

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${sessionToken}` }),
    [sessionToken],
  );

  const fetchMySubmissions = useCallback(async () => {
    try {
      const res = await apiClient.get(
        API_ENDPOINTS.publicOfferLinkMySubmissions(linkId),
        { headers: authHeaders() },
      );
      const list = Array.isArray(res.data) ? res.data : [];
      setSubmissions(list);
      return list;
    } catch (e) {
      console.error('Failed to load my submissions:', e);
      return [];
    }
  }, [linkId, authHeaders]);

  useEffect(() => {
    if (!sessionToken) {
      setLoadError('This upload page is missing its access token. Please re-verify from the previous step.');
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [meRes, mineRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.publicOfferLinkMe(linkId), { headers: authHeaders() }),
          apiClient.get(API_ENDPOINTS.publicOfferLinkMySubmissions(linkId), { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        setMe(meRes.data);
        setSubmissions(Array.isArray(mineRes.data) ? mineRes.data : []);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          // Cached session was rejected (agent disabled the link, token
          // revoked, or secret rotated). Drop the cache so the next visit
          // doesn't bounce straight back here from the recipient page's
          // auto-skip effect.
          try { localStorage.removeItem(`offerLinkSession:${linkId}`); } catch { /* noop */ }
        }
        // For 401s always use the "expired" wording — the backend's raw
        // "Invalid session token" is accurate but reads scarier than the
        // user's mental model (they were signed in, now they're not).
        const msg = status === 401
          ? 'Your session has expired. Please verify again.'
          : (e?.response?.data?.message || 'Could not load your session.');
        setLoadError(msg);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionToken, linkId, authHeaders]);

  const handleReverify = () => {
    // Clear BOTH the legacy sessionStorage key and the current localStorage
    // key so switching accounts / re-verifying always starts fresh.
    try { localStorage.removeItem(`offerLinkSession:${linkId}`); } catch { /* noop */ }
    try { sessionStorage.removeItem(`offerLinkSession:${linkId}`); } catch { /* noop */ }
    navigate(offerBasePath);
  };

  const addFiles = (incoming) => {
    const next = [...files];
    for (const file of incoming) {
      // Reject non-PDFs client-side. The server double-checks mime + magic
      // bytes; this is for immediate feedback.
      const isPdf = file.type === 'application/pdf'
          || (file.name || '').toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        setUploadError(`"${file.name}" is not a PDF.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setUploadError(`"${file.name}" exceeds the 25 MB limit.`);
        continue;
      }
      next.push(file);
    }
    setFiles(next);
  };

  const handleFileSelect = (evt) => {
    setUploadError('');
    addFiles(Array.from(evt.target.files || []));
    evt.target.value = '';
  };

  const handleDrop = (evt) => {
    evt.preventDefault();
    setIsDragging(false);
    setUploadError('');
    addFiles(Array.from(evt.dataTransfer.files || []));
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setUploadError('Add at least one PDF to upload.');
      return;
    }
    if (mode === 'add' && !targetOfferId) {
      setUploadError('Pick an offer to add documents to.');
      return;
    }
    if (mode === 'new') {
      // Same checks as the internal Upload Offer Package form.
      const price = Number(offerDetails.offerPrice.replace(/[^0-9.]/g, ''));
      if (!offerDetails.offerPrice.trim()) return setUploadError('Offer price is required.');
      if (!price || price <= 0) return setUploadError('Offer price must be a positive number.');
      if (offerDetails.downPaymentPercent.trim()) {
        const dp = Number(offerDetails.downPaymentPercent.replace(/[^0-9.]/g, ''));
        if (isNaN(dp) || dp < 0 || dp > 100) return setUploadError('Down payment must be between 0 and 100.');
      }
      if (offerDetails.closingDays.trim()) {
        const coe = Number(offerDetails.closingDays.replace(/[^0-9]/g, ''));
        if (!coe || coe <= 0) return setUploadError('Closing days must be a positive number.');
      }
    }
    setUploadError('');
    setJustSubmittedMessage('');
    setIsUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      if (mode === 'new') {
        formData.append('offerPrice', offerDetails.offerPrice.replace(/[^0-9.]/g, ''));
        if (offerDetails.earnestMoney.trim())
          formData.append('earnestMoney', offerDetails.earnestMoney.replace(/[^0-9.]/g, ''));
        if (offerDetails.downPaymentPercent.trim())
          formData.append('downPaymentPercent', offerDetails.downPaymentPercent.replace(/[^0-9.]/g, ''));
        formData.append('financingType', offerDetails.financingType);
        if (offerDetails.closingDays.trim())
          formData.append('closingDays', offerDetails.closingDays.replace(/[^0-9]/g, ''));
        if (offerDetails.notes.trim())
          formData.append('notes', offerDetails.notes.trim());
      }
      const query = mode === 'add' && targetOfferId
        ? `?offerId=${encodeURIComponent(targetOfferId)}`
        : '';
      await apiClient.post(
        `${API_ENDPOINTS.publicOfferLinkUpload(linkId)}${query}`,
        formData,
        {
          headers: {
            ...authHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      setFiles([]);
      setOfferDetails(emptyOfferDetails);
      const updated = await fetchMySubmissions();
      setJustSubmittedMessage(mode === 'add'
        ? 'Documents added to your offer.'
        : 'Your offer was submitted.');
      // Reset to new-offer mode by default; if the user wants to append again
      // they can re-pick explicitly.
      setMode('new');
      setTargetOfferId('');
      // Keep refreshed list visible; nothing more to do.
      void updated;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        try { localStorage.removeItem(`offerLinkSession:${linkId}`); } catch { /* noop */ }
        setUploadError('Your session has expired. Please verify again.');
      } else {
        setUploadError(e?.response?.data?.message || 'Upload failed. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddToOffer = (offerId) => {
    setMode('add');
    setTargetOfferId(offerId);
    setUploadError('');
    setJustSubmittedMessage('');
    // Scroll the file picker into view so the next action is obvious.
    setTimeout(() => fileInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  };

  const formatSubmissionTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="offer-upload-shell">
      <header className="offer-upload-header">
        <div className="offer-upload-header-inner">
          <div className="offer-upload-brand">Submit an Offer</div>
          {me && (
            <div className="offer-upload-greeting-wrap">
              <div className="offer-upload-greeting">
                Hi, <strong>{me.firstName || 'there'}</strong>
              </div>
              <button
                type="button"
                className="offer-upload-switch-btn"
                onClick={handleReverify}
                title="Switch to a different email"
              >
                Not you?
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="offer-upload-main">
        {isLoading && <p className="offer-upload-status">Loading…</p>}

        {!isLoading && loadError && (
          <div className="offer-upload-error-card">
            <h2>Access issue</h2>
            <p>{loadError}</p>
            <button className="offer-upload-primary" onClick={handleReverify}>Verify again</button>
          </div>
        )}

        {!isLoading && !loadError && me && (
          <>
            <section className="offer-upload-context">
              <div>
                <div className="offer-upload-label">Property</div>
                <div className="offer-upload-value">{me.propertyAddress || '—'}</div>
              </div>
              <div>
                <div className="offer-upload-label">Listing agent</div>
                <div className="offer-upload-value">
                  {me.agentName || '—'}
                  {me.agentBrokerage ? `, ${me.agentBrokerage}` : ''}
                </div>
              </div>
            </section>

            {submissions.length > 0 && (
              <section className="offer-upload-submissions">
                <h2>Your submitted offers</h2>
                <ul className="offer-upload-submission-list">
                  {submissions.map((s) => (
                    <li
                      key={s.offerId}
                      className={`offer-upload-submission-item${targetOfferId === s.offerId ? ' selected' : ''}`}
                    >
                      <div className="offer-upload-submission-head">
                        <span className="offer-upload-submission-check" aria-hidden="true">✓</span>
                        <span className="offer-upload-submission-title">
                          Submitted {formatSubmissionTime(s.submittedAt)}
                        </span>
                        <span className="offer-upload-submission-count">
                          {s.documentCount} {s.documentCount === 1 ? 'doc' : 'docs'}
                        </span>
                      </div>
                      {s.lastDocumentAddedAt && (
                        <div className="offer-upload-submission-updated">
                          Last updated {formatSubmissionTime(s.lastDocumentAddedAt)}
                        </div>
                      )}
                      {(s.fileNames || []).length > 0 && (
                        <ol className="offer-upload-submission-files">
                          {s.fileNames.map((name, i) => (
                            <li key={i} className="offer-upload-submission-file">
                              <span className="offer-upload-submission-file-num">{i + 1}.</span>
                              <span className="offer-upload-submission-file-name">{name}</span>
                            </li>
                          ))}
                        </ol>
                      )}
                      <button
                        type="button"
                        className="offer-upload-add-btn"
                        onClick={() => handleAddToOffer(s.offerId)}
                        disabled={isUploading}
                      >
                        + Add documents
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="offer-upload-dropzone-wrap">
              <h2>{mode === 'add' ? 'Add documents to your offer' : 'Upload your offer package'}</h2>
              <p className="offer-upload-help">
                PDF only &middot; up to 25 MB per file. You can select multiple files (e.g. Purchase Agreement, Pre-Approval, Addenda).
              </p>

              {/* Mode picker — only shown when there is at least one past
                  submission. 'add' option is disabled otherwise. */}
              {submissions.length > 0 && (
                <div className="offer-upload-mode-picker">
                  <label className={`offer-upload-mode-chip${mode === 'new' ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="offer-upload-mode"
                      value="new"
                      checked={mode === 'new'}
                      onChange={() => { setMode('new'); setTargetOfferId(''); }}
                      disabled={isUploading}
                    />
                    <span className="offer-upload-mode-title">New offer</span>
                    <span className="offer-upload-mode-sub">Start a separate offer package</span>
                  </label>
                  <label className={`offer-upload-mode-chip${mode === 'add' ? ' selected' : ''}`}>
                    <input
                      type="radio"
                      name="offer-upload-mode"
                      value="add"
                      checked={mode === 'add'}
                      onChange={() => setMode('add')}
                      disabled={isUploading}
                    />
                    <span className="offer-upload-mode-title">Add to existing offer</span>
                    <span className="offer-upload-mode-sub">Append docs to one of your past submissions</span>
                  </label>
                </div>
              )}

              {mode === 'add' && submissions.length > 0 && (
                <div className="offer-upload-target-row">
                  <label htmlFor="offer-upload-target" className="offer-upload-target-label">
                    Pick the offer
                  </label>
                  <select
                    id="offer-upload-target"
                    className="offer-upload-target-select"
                    value={targetOfferId}
                    onChange={(e) => setTargetOfferId(e.target.value)}
                    disabled={isUploading}
                  >
                    <option value="">— Select —</option>
                    {submissions.map((s) => (
                      <option key={s.offerId} value={s.offerId}>
                        Offer from {formatSubmissionTime(s.submittedAt)} ({s.documentCount} {s.documentCount === 1 ? 'doc' : 'docs'})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {mode === 'new' && (
                <div className="offer-upload-details">
                  <div className="offer-upload-details-title">Offer details</div>
                  <label className="offer-upload-field offer-upload-field-full">
                    <span className="offer-upload-target-label">Offer price <span className="offer-upload-required">*</span></span>
                    <input type="text" inputMode="decimal" className="offer-upload-input" placeholder="e.g. 890000"
                      value={offerDetails.offerPrice} onChange={setDetail('offerPrice')} disabled={isUploading} />
                  </label>
                  <label className="offer-upload-field">
                    <span className="offer-upload-target-label">Earnest money (EMD)</span>
                    <input type="text" inputMode="decimal" className="offer-upload-input" placeholder="e.g. 15000"
                      value={offerDetails.earnestMoney} onChange={setDetail('earnestMoney')} disabled={isUploading} />
                  </label>
                  <label className="offer-upload-field">
                    <span className="offer-upload-target-label">Down payment %</span>
                    <input type="text" inputMode="decimal" className="offer-upload-input" placeholder="e.g. 20"
                      value={offerDetails.downPaymentPercent} onChange={setDetail('downPaymentPercent')} disabled={isUploading} />
                  </label>
                  <div className="offer-upload-field offer-upload-field-full">
                    <span className="offer-upload-target-label">Financing type</span>
                    <div className="offer-upload-financing">
                      {['CONVENTIONAL', 'FHA', 'VA', 'CASH', 'OTHER'].map((ft) => (
                        <button key={ft} type="button" disabled={isUploading}
                          className={`offer-upload-financing-chip${offerDetails.financingType === ft ? ' selected' : ''}`}
                          onClick={() => setOfferDetails((prev) => ({ ...prev, financingType: ft }))}>
                          {ft.charAt(0) + ft.slice(1).toLowerCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="offer-upload-field offer-upload-field-full">
                    <span className="offer-upload-target-label">Close of escrow (days)</span>
                    <input type="text" inputMode="numeric" className="offer-upload-input"
                      value={offerDetails.closingDays} onChange={setDetail('closingDays')} disabled={isUploading} />
                  </label>
                  <label className="offer-upload-field offer-upload-field-full">
                    <span className="offer-upload-target-label">Notes</span>
                    <textarea className="offer-upload-input offer-upload-textarea" rows={3} maxLength={2000}
                      placeholder="Any additional notes about this offer..."
                      value={offerDetails.notes} onChange={setDetail('notes')} disabled={isUploading} />
                  </label>
                </div>
              )}

              <div
                className={`offer-upload-dropzone${isDragging ? ' drag-over' : ''}${isUploading ? ' disabled' : ''}`}
                onDragOver={(e) => { e.preventDefault(); if (!isUploading) setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={isUploading ? (e) => e.preventDefault() : handleDrop}
                onClick={() => { if (!isUploading) fileInputRef.current?.click(); }}
              >
                <div className="offer-upload-dropzone-icon">📁</div>
                <div className="offer-upload-dropzone-title">Click to upload or drag PDF files here</div>
                <div className="offer-upload-dropzone-sub">Multiple files allowed</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {files.length > 0 && (
                <ul className="offer-upload-file-list">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="offer-upload-file-item">
                      <span className="offer-upload-file-icon">📄</span>
                      <span className="offer-upload-file-name">{f.name}</span>
                      <span className="offer-upload-file-size">{formatBytes(f.size)}</span>
                      <button
                        type="button"
                        className="offer-upload-file-remove"
                        onClick={() => removeFile(i)}
                        disabled={isUploading}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {uploadError && <p className="offer-upload-error-inline">{uploadError}</p>}
              {justSubmittedMessage && !uploadError && (
                <p className="offer-upload-success-inline">{justSubmittedMessage}</p>
              )}

              <button
                type="button"
                className="offer-upload-primary"
                onClick={handleUpload}
                disabled={isUploading || files.length === 0 || (mode === 'add' && !targetOfferId)}
              >
                {isUploading
                  ? 'Uploading…'
                  : mode === 'add'
                    ? `Add ${files.length || ''} file${files.length === 1 ? '' : 's'} to offer`.trim()
                    : `Submit ${files.length || ''} file${files.length === 1 ? '' : 's'}`.trim()}
              </button>
            </section>
          </>
        )}
      </main>

      <footer className="offer-upload-footer">
        <p>Uploads are logged for legal audit and shared only with the listing agent and the assigned transaction coordinator.</p>
      </footer>
    </div>
  );
};

export default OfferUploadPage;
